import { persistantStore } from '../store/persistantStore';

export const DASHBOARD_SHARE_VERSION = 2;

type Setter<T> = (value: T) => void;

export function applyShareFields(
  obj: Record<string, unknown>,
  fields: Record<string, Setter<unknown>>
): void {
  for (const [key, setter] of Object.entries(fields)) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      setter(obj[key]);
    }
  }
}

export function applyStringField(obj: Record<string, unknown>, key: string, setter: Setter<string>): void {
  if (typeof obj[key] === 'string') {
    setter(obj[key] as string);
  }
}

export function applyNumberField(obj: Record<string, unknown>, key: string, setter: Setter<number>): void {
  const value = obj[key];
  if (typeof value === 'number' && Number.isFinite(value)) {
    setter(value);
  }
}

export function applyBooleanField(obj: Record<string, unknown>, key: string, setter: Setter<boolean>): void {
  if (typeof obj[key] === 'boolean') {
    setter(obj[key] as boolean);
  }
}

export function applyStringArrayField(obj: Record<string, unknown>, key: string, setter: Setter<string[]>): void {
  if (Array.isArray(obj[key])) {
    setter((obj[key] as unknown[]).map((x) => String(x)));
  }
}

export function applyNumberArrayField(obj: Record<string, unknown>, key: string, setter: Setter<number[]>): void {
  if (Array.isArray(obj[key])) {
    setter(
      (obj[key] as unknown[]).filter((x): x is number => typeof x === 'number' && Number.isFinite(x))
    );
  }
}

export function applyRecordField(
  obj: Record<string, unknown>,
  key: string,
  setter: Setter<Record<string, string>>
): void {
  const value = obj[key];
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    setter(value as Record<string, string>);
  }
}

export function applySortConfigField(
  obj: Record<string, unknown>,
  key: string,
  setter: Setter<{ key: string; direction: 'asc' | 'desc' }>
): void {
  const value = obj[key];
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const rec = value as Record<string, unknown>;
    if (typeof rec.key === 'string' && (rec.direction === 'asc' || rec.direction === 'desc')) {
      setter({ key: rec.key, direction: rec.direction });
    }
  }
}

export function validateSharePage(obj: Record<string, unknown>, pageKey: string): boolean {
  const ver = Number(obj.v);
  return obj.page === pageKey && (ver === 1 || ver === 2);
}

export interface RouteShareSetters {
  setSelectedProjectId?: (value: number) => void;
  setSelectedClassName?: (value: string) => void;
  setSelectedDatasetId?: (value: number) => void;
  setSelectedDate?: (value: string) => void;
  setSelectedSourceId?: (value: number) => void;
  setSelectedMenu?: (value: string) => void;
  setSelectedPage?: (value: string) => void;
}

export function applyRouteFromShare(obj: Record<string, unknown>, setters?: RouteShareSetters): void {
  const s = setters ?? {
    setSelectedProjectId: persistantStore.setSelectedProjectId,
    setSelectedClassName: persistantStore.setSelectedClassName,
    setSelectedDatasetId: persistantStore.setSelectedDatasetId,
    setSelectedDate: persistantStore.setSelectedDate,
    setSelectedSourceId: persistantStore.setSelectedSourceId,
    setSelectedMenu: persistantStore.setSelectedMenu,
    setSelectedPage: persistantStore.setSelectedPage,
  };

  applyStringField(obj, 'className', s.setSelectedClassName!);
  applyNumberField(obj, 'projectId', s.setSelectedProjectId!);
  applyNumberField(obj, 'datasetId', s.setSelectedDatasetId!);
  applyStringField(obj, 'date', s.setSelectedDate!);
  applyNumberField(obj, 'sourceId', s.setSelectedSourceId!);
  applyStringField(obj, 'selectedMenu', s.setSelectedMenu!);
  applyStringField(obj, 'selectedPage', s.setSelectedPage!);
}
